/**
 * Reads a JSON body under an expected shape.
 *
 * `Response.json()` resolves to `unknown`, which is correct — the network can
 * return anything. This helper does **not** validate; it makes the assumption
 * explicit and local instead of letting an untyped value spread through the
 * rest of the function. Callers should still treat every field as optional and
 * guard before use, which is why most call sites type `T` with `?` on
 * everything.
 *
 * Use Zod where the payload crosses a trust boundary or drives a mutation.
 */
export async function readJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

/**
 * Same, but never throws: a non-JSON body (an HTML error page, an empty 502)
 * resolves to `null` rather than blowing up the caller's `try`.
 */
export async function readJsonSafe<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

/**
 * `JSON.parse` under `@total-typescript/ts-reset` returns `unknown`, which is
 * right. Same trade-off as `readJson`: state the shape, keep the fields
 * optional, and guard at the point of use.
 */
export function parseJson<T>(text: string): T {
  return JSON.parse(text) as T;
}

/** As `parseJson`, but malformed input yields `null` instead of throwing. */
export function parseJsonSafe<T>(text: string): T | null {
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}
